import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Quote } from '../../types/quote';

import { useQuoteStore } from '../../stores/quoteStore';
import { Button } from '../common/Button';
import ScrollableContainer from '../common/ScrollableContainer';
import GridTable from '../common/GridTable';
import { formatDateLocalized } from '../../utils/dateUtils';
import i18n from '../../i18n';
import ProfileAvatar from '../common/ProfileAvatar';
import { getQuoteStatus } from '../../utils/quoteUtils';
import AlliancesHeadshots from '../alliances/AlliancesHeadshots';
import EyeButton from '../common/EyeButton';
import SingleSubmissionModal from '../quotes/SingleSubmissionModal';
import { LoadingSpinner } from '../LoadingSpinner';

const QUOTES_COUNT = 3;

const QuotesSection: React.FC = () => {
  const { t } = useTranslation(['jobs', 'quotes', 'dashboard']);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const { loading, quotes, fetchQuotes } = useQuoteStore();

  const visibleQuotes = quotes.slice(0, QUOTES_COUNT);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const closeModal = () => setSelectedQuote(null);

  return (
    <section className="border-2 rounded-2xl border-gray-300 bg-white p-6 shadow-xl md:rounded-2xl md:p-8">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="relative font-bold text-secondary">
          <span className="text-2xl md:text-[1.375rem]">{t('dashboard:submissions.title')}</span>
        </h1>
        <Button variant="transparent-with-border" href="/quotes" className="hidden md:inline-flex">
          {t('dashboard:submissions.view_all')}
        </Button>
      </div>

      {loading && visibleQuotes.length === 0 && (
        <div className="flex h-10 items-center justify-center">
          <LoadingSpinner />
        </div>
      )}

      {visibleQuotes.length === 0 && !loading && (
        <div className="mb-4 flex h-10 items-center justify-center text-sm text-gray-500">
          {t('quotes:noQuotes')}
        </div>
      )}

      {visibleQuotes.length > 0 && (
        <ScrollableContainer>
          <GridTable className="grid-cols-[6fr_2fr_1fr_2fr_1fr_1fr] divide-y-[2px] divide-[#E6EDF4]">
            {/* Grid Header */}
            <GridTable.Cell isHeader className="!border-t-0 pl-0">
              {t('dashboard:description')}
            </GridTable.Cell>
            <GridTable.Cell isHeader className="!border-t-0">
              {t('dashboard:price_range')}
            </GridTable.Cell>
            <GridTable.Cell isHeader className="!border-t-0">
              {t('dashboard:quoteStatus')}
            </GridTable.Cell>
            <GridTable.Cell isHeader className="!border-t-0">
              {t('dashboard:date')}
            </GridTable.Cell>
            <GridTable.Cell isHeader className="!border-t-0">
              {t('dashboard:alliances')}
            </GridTable.Cell>
            <GridTable.Cell isHeader className="justify-end !border-t-0 pr-0">
              {' '}
            </GridTable.Cell>

            {/* Grid Rows */}
            {visibleQuotes.map(quote => {
              const clientName = quote.job.client.company_name || quote.job.client.first_name;

              const priceRange = t(`common:budget_ranges.${quote.job.budget_range.code}`, {
                defaultValue: quote.job.budget_range.code,
              });

              const quoteStatus = getQuoteStatus(quote);
              const quoteStatusLabel = t(`quote_statuses.${quoteStatus}`, { ns: 'common' });
              const quoteDate = formatDateLocalized(
                quote.submitted_at || quote.updated_at || quote.created_at,
                i18n.language,
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              );

              return (
                <React.Fragment key={quote.id}>
                  <GridTable.Cell
                    className="cursor-pointer gap-4 pl-0"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <ProfileAvatar
                      profile_id={quote.job.client.id}
                      first_name={clientName}
                      avatar_url={quote.job.client.avatar_url}
                      className="flex-shrink-0"
                    />
                    <div className="text-secondary">
                      <div className="font-bold leading-tight">{clientName}</div>
                      <div className="text-sm leading-tight">{quote.job.title}</div>
                    </div>
                  </GridTable.Cell>

                  <GridTable.Cell
                    className="cursor-pointer text-sm"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    {priceRange}
                  </GridTable.Cell>

                  <GridTable.Cell
                    className="cursor-pointer text-sm"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    {quoteStatusLabel}
                  </GridTable.Cell>

                  <GridTable.Cell
                    className="cursor-pointer text-sm"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    {quoteDate}
                  </GridTable.Cell>

                  <GridTable.Cell
                    className="cursor-pointer"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    {quote.alliances.length > 0 ? (
                      <AlliancesHeadshots
                        alliances={quote.alliances.map(alliance => alliance.invited_freelancer)}
                        allianceCount={quote.alliances.length}
                        avatarSize="w-5 h-5"
                      />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </GridTable.Cell>

                  <GridTable.Cell
                    className="cursor-pointer justify-end pr-0"
                    role="button"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <EyeButton
                      ariaLabel={t('viewSubmission', { ns: 'projects' })}
                      onClick={() => setSelectedQuote(quote)}
                    />
                  </GridTable.Cell>
                </React.Fragment>
              );
            })}
          </GridTable>
        </ScrollableContainer>
      )}

      {selectedQuote && (
        <SingleSubmissionModal onClose={closeModal} job={selectedQuote.job} quote={selectedQuote} />
      )}

      <div className="mt-4 sm:hidden">
        <Link
          to="/quotes"
          className="block rounded-full border border-primary px-6 py-2 text-center font-bold text-secondary"
        >
          {t('dashboard:submissions.view_all')}
        </Link>
      </div>
    </section>
  );
};

export default QuotesSection;
